import React, { useMemo, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/format";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const pad = (n) => String(n).padStart(2, "0");
const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtLong = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};
const fmt12 = (hh, mm) => {
  const h = hh % 12 || 12;
  return `${h}:${pad(mm)} ${hh < 12 ? "AM" : "PM"}`;
};

// Ride pickups are only offered between 07:00 (7 AM) and 21:00 (9 PM).
const TIME_SLOTS = [];
for (let h = 7; h <= 21; h++) {
  for (let m = 0; m < 60; m += 15) {
    if (h === 21 && m > 0) break; // last slot is 9:00 PM
    TIME_SLOTS.push({ value: `${pad(h)}:${pad(m)}`, label: fmt12(h, m) });
  }
}

// Emits combined "YYYY-MM-DDTHH:mm" (24h, empty until both chosen).
// Date shows as "15 December 2026"; time shows as 12-hour AM/PM.
export default function DateTimeFields({ min, onChange, testIDPrefix = "schedule" }) {
  const [date, setDate] = useState(""); // YYYY-MM-DD
  const [time, setTime] = useState(""); // HH:mm (24h)
  const [openDate, setOpenDate] = useState(false);
  const [openTime, setOpenTime] = useState(false);
  const minDate = min ? min.split("T")[0] : null;
  const [view, setView] = useState(() => (minDate ? new Date(minDate + "T00:00") : new Date()));
  const wrapRef = useRef(null);
  const dateBtnRef = useRef(null);
  const timeBtnRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (e.target.closest && e.target.closest("[data-dtf-pop]")) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpenDate(false); setOpenTime(false); }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Fixed position anchored to the trigger; opens above when there's room, else below.
  const popStyle = (ref, width) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return { display: "none" };
    const openAbove = r.top > 360;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    return openAbove
      ? { position: "fixed", left, bottom: window.innerHeight - r.top + 8, zIndex: 200 }
      : { position: "fixed", left, top: r.bottom + 8, zIndex: 200 };
  };
  const timePopStyle = () => {
    const r = timeBtnRef.current?.getBoundingClientRect();
    if (!r) return { display: "none" };
    const width = 160;
    const openAbove = r.top > 320;
    const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
    return openAbove
      ? { position: "fixed", left, bottom: window.innerHeight - r.top + 8, zIndex: 200 }
      : { position: "fixed", left, top: r.bottom + 8, zIndex: 200 };
  };

  const emit = (nd, nt) => onChange(nd && nt ? `${nd}T${nt}` : "");

  const grid = useMemo(() => {
    const y = view.getFullYear(), m = view.getMonth();
    const first = new Date(y, m, 1).getDay();
    const days = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(y, m, d));
    return cells;
  }, [view]);

  const pickDay = (d) => { const s = toStr(d); setDate(s); emit(s, time); setOpenDate(false); };
  const pickTime = (v) => { setTime(v); emit(date, v); setOpenTime(false); };

  const timeLabel = time ? fmt12(...time.split(":").map(Number)) : "Time";

  return (
    <div ref={wrapRef} className="grid grid-cols-2 gap-3">
      {/* Date */}
      <div className="relative">
        <span className="mb-1.5 block text-sm font-medium text-foreground/80">Date</span>
        <button
          type="button"
          ref={dateBtnRef}
          data-testid={`${testIDPrefix}-date`}
          onClick={() => { setOpenDate((o) => !o); setOpenTime(false); }}
          className="flex h-12 w-full items-center gap-2 rounded-xl bg-secondary px-3 text-left"
        >
          <CalendarIcon className="h-5 w-5 shrink-0 text-foreground" />
          <span className={cn("truncate text-sm font-medium", date ? "text-foreground" : "text-muted-foreground")}>
            {date ? fmtLong(date) : "Date"}
          </span>
        </button>
        {openDate && createPortal(
          <div data-dtf-pop style={popStyle(dateBtnRef, 288)} className="w-72 rounded-2xl border border-border bg-card p-3 shadow-card">
            <div className="mb-2 flex items-center justify-between">
              <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-secondary">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-bold text-foreground">{MONTHS[view.getMonth()]} {view.getFullYear()}</span>
              <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-secondary">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="mb-1 grid grid-cols-7 text-center text-[11px] font-semibold text-muted-foreground">
              {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {grid.map((d, i) => {
                if (!d) return <span key={i} />;
                const s = toStr(d);
                const disabled = minDate && s < minDate;
                const selected = s === date;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    onClick={() => pickDay(d)}
                    className={cn(
                      "flex h-9 items-center justify-center rounded-lg text-sm font-medium transition-colors",
                      selected ? "bg-brand-600 text-white" : "text-foreground hover:bg-accent",
                      disabled && "cursor-not-allowed opacity-30 hover:bg-transparent"
                    )}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Time */}
      <div className="relative">
        <span className="mb-1.5 block text-sm font-medium text-foreground/80">Time</span>
        <button
          type="button"
          ref={timeBtnRef}
          data-testid={`${testIDPrefix}-time`}
          onClick={() => { setOpenTime((o) => !o); setOpenDate(false); }}
          className="flex h-12 w-full items-center gap-2 rounded-xl bg-secondary px-3 text-left"
        >
          <Clock className="h-5 w-5 shrink-0 text-foreground" />
          <span className={cn("flex-1 truncate text-sm font-medium", time ? "text-foreground" : "text-muted-foreground")}>{timeLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
        {openTime && createPortal(
          <div data-dtf-pop style={timePopStyle()} className="max-h-64 w-40 overflow-y-auto rounded-2xl border border-border bg-card p-1.5 shadow-card">
            {TIME_SLOTS.map((t) => (
              <button
                key={t.value}
                type="button"
                data-testid={`time-slot-${t.value}`}
                onClick={() => pickTime(t.value)}
                className={cn(
                  "block w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
                  time === t.value ? "bg-brand-600 text-white" : "text-foreground hover:bg-accent"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}
